import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { ScrollReveal } from "./MaskedReveal";
import { CinemaPlayer } from "./CinemaPlayer";
import { FILMS } from "../data/siteData";

const useDragScroll = (ref) => {
  const dragging = useRef(false);
  const moved = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);

  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return;
    dragging.current = true;
    moved.current = false;
    startX.current = e.clientX;
    startScroll.current = ref.current.scrollLeft;
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - startX.current;
    if (Math.abs(dx) > 6) moved.current = true;
    ref.current.scrollLeft = startScroll.current - dx;
  };
  const endDrag = () => {
    dragging.current = false;
  };
  const onClickCapture = (e) => {
    if (moved.current) {
      e.stopPropagation();
      e.preventDefault();
      moved.current = false;
    }
  };
  return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerLeave: endDrag, onClickCapture };
};

const FilmCard = ({ film, index, onPlay }) => (
  <motion.article
    data-testid={`film-card-${film.id}`}
    className="group w-[78vw] shrink-0 select-none sm:w-[420px]"
    whileHover={{ y: -8 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
  >
    <button
      data-testid={`film-play-button-${film.id}`}
      onClick={() => onPlay(film)}
      aria-label={`Пусни ${film.title}`}
      className="relative block w-full cursor-pointer overflow-hidden rounded-3xl border border-[#14110D]/15 text-left"
    >
      <img
        src={film.image}
        alt={`${film.title} — кадър от филма`}
        draggable={false}
        className="pointer-events-none aspect-[4/3] w-full object-cover grayscale transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <span className="pointer-events-none absolute inset-0 bg-[#14110D]/0 transition-colors duration-500 group-hover:bg-[#14110D]/25" />
      <span className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-full bg-[#F4F1EA] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#14110D] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <Play className="h-3 w-3 fill-current" /> Пусни ↗
      </span>
      <span className="pointer-events-none absolute bottom-4 right-4 rounded-full bg-[#14110D]/85 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F1EA]">
        {film.year}
      </span>
    </button>
    <div className="mt-4 flex items-start justify-between px-1">
      <div>
        <h3 className="font-serif text-2xl text-[#14110D] transition-colors duration-300 group-hover:text-[#C8402A]">
          {film.title}
        </h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C867E]">{film.category}</p>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C867E]">
        {String(index + 1).padStart(2, "0")} / {String(FILMS.length).padStart(2, "0")}
      </span>
    </div>
  </motion.article>
);

export const SelectedWorks = () => {
  const trackRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  const dragHandlers = useDragScroll(trackRef);

  return (
    <section id="raboti" className="relative overflow-hidden border-t border-[#14110D]/10 py-24" data-testid="works-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <ScrollReveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#C8402A]">§ 01</p>
              <h2 className="mt-2 font-serif text-4xl uppercase tracking-tight text-[#14110D] sm:text-5xl lg:text-6xl">
                Избрани работи
              </h2>
            </div>
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867E]">
              Моите услуги · Плъзни надясно <ArrowUpRight className="h-3.5 w-3.5 rotate-45" />
            </p>
          </div>
        </ScrollReveal>
      </div>

      <ScrollReveal delay={0.15} className="mt-12">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8" data-lenis-prevent>
          <div
            ref={trackRef}
            data-testid="works-slider"
            className="works-scroll flex cursor-grab snap-x snap-proximity gap-6 overflow-x-auto pb-2 active:cursor-grabbing"
            {...dragHandlers}
          >
            {FILMS.map((film, i) => (
              <div key={film.id} className="snap-start">
                <FilmCard film={film} index={i} onPlay={setPlaying} />
              </div>
            ))}
          </div>
        </div>
      </ScrollReveal>

      {createPortal(<CinemaPlayer film={playing} onClose={() => setPlaying(null)} />, document.body)}
    </section>
  );
};
